import axios from "axios";
import { getAuthorization, getRefreshToken, setAuthorization } from "config/service/auth-service";


let isMaintenance = false;
let isRefreshing = false;
let failedRequestQueue = [];
let cancelTokenSource;
let cancelRequestQueue = [];


const setupInterceptors = () => {
  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  axios.defaults.responseType = "json";
  axios.defaults.headers["Accept"] = "*/*";
  axios.defaults.headers["Content-Type"] = "application/json";

  axios.interceptors.request.use(
    config => {

      cancelTokenSource = axios.CancelToken.source();
      config.cancelToken = cancelTokenSource.token;
      cancelRequestQueue.push(cancelTokenSource);

      const token = getAuthorization();
      if (token) {
        config.headers.Authorization = `${token}`;
      }
      if (config.data === undefined) {
        config.data = null;
      }
      return config;
    },
    error => Promise.reject(error)
  )

  axios.interceptors.response.use(response => {

    cancelRequestQueue = cancelRequestQueue.filter(
      cancelTokenSource => cancelTokenSource.token !== response.config.cancelToken
    );

    if (response.data && (response.data.success || response.data.success === undefined)) {
      if (isMaintenance) {
        clearMaintenance();
      }

      return response;
    } else {
      throw (response)
    }
  }, async (error) => {

    const originalRequest = error.config;

    cancelRequestQueue = cancelRequestQueue.filter(
      cancelTokenSource => cancelTokenSource.token !== originalRequest.cancelToken
    );

    if (axios.isCancel(error)) {
      return new Promise(() => { });
    }

    if (error.response && error.response.status === 503) {
      if (originalRequest.url === "/system-maintenance") {
        return Promise.reject(error);
      }

      if (!isMaintenance) {
        isMaintenance = true;
      }

      redirectToMaintenancePage();

      return new Promise(() => { });
    }

    if (error.response && error.response.status === 401) {
      const accessToken = getAuthorization();
      const refreshToken = getRefreshToken();

      if (refreshToken && !isRefreshing) {
        isRefreshing = true;
        try {
          const client = axios.create();
          const { data } = await client.post(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
            accessToken: accessToken,
            refreshToken: refreshToken,
          });

          const newAccessToken = data.accessToken;
          const newRefreshToken = data.refreshToken;
          originalRequest.headers.Authorization = newAccessToken;
          setAuthorization(newAccessToken, newRefreshToken);

          failedRequestQueue.forEach((request) => request.onSuccess(newAccessToken));
          failedRequestQueue = [];
          return axios(originalRequest);
        } catch (refreshError) {
          failedRequestQueue.forEach((request) => request.onFailure(refreshError));
          failedRequestQueue = [];
          clearStorageAndRedirect();
        } finally {
          isRefreshing = false;
        }

      } else if (refreshToken) {
        return new Promise((resolve, reject) => {
          failedRequestQueue.push({
            onSuccess: (token) => {
              originalRequest.headers.Authorization = token;
              resolve(axios(originalRequest));
            },
            onFailure: (err) => {
              reject(err);
            },
          });
        });
      }

      clearStorageAndRedirect();
    }

    return Promise.reject(error);
  });
}

export const cancelAllRequests = () => {
  cancelRequestQueue.forEach((cancelTokenSource) => {
    cancelTokenSource.cancel();
  });
  cancelRequestQueue = [];
};

export const clearStorageAndRedirect = () => {
  cancelAllRequests();
  localStorage.clear();
  window.location.href = "/auth/login";
}

const redirectToMaintenancePage = () => {
  cancelAllRequests();
  window.location.href = "/system-maintenance";
};

const clearMaintenance = () => {
  isMaintenance = false;
}

export { setupInterceptors };
